import React, { useState, useEffect, useCallback } from 'react';
import { PlusCircle, Eye, CreditCard, Edit3, Trash2, FileText, XCircle, ListChecks } from 'lucide-react';
import { Modal, ConfirmationModal } from './Helpers';
import { deleteDuesForPeriod } from '../api/periodicalDuesApi';
import '../styles/CommonStyles.css';

const GenerateDuesView = ({ showToast, currentUser, setCurrentView }) => {
  const [duesDefinitions, setDuesDefinitions] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [people, setPeople] = useState([]);
  const [periodicalDuesSummaries, setPeriodicalDuesSummaries] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [selectedPeriodForGeneration, setSelectedPeriodForGeneration] = useState('');
  const [selectedBlockForGeneration, setSelectedBlockForGeneration] = useState('');
  const [distributionPeriod, setDistributionPeriod] = useState('');
  const [distributionMeterType, setDistributionMeterType] = useState('yakit');
  const [distributionTotalCost, setDistributionTotalCost] = useState('');
  const [distributionScope, setDistributionScope] = useState('building');
  const [distributionBlockId, setDistributionBlockId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);

  // Filters for the summary list
  const [filterBlockId, setFilterBlockId] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('');
  const [filterPersonId, setFilterPersonId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterResponsiblePartyType, setFilterResponsiblePartyType] = useState('');

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [dueSummaryToPay, setDueSummaryToPay] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [breakdownModalOpen, setBreakdownModalOpen] = useState(false);
  const [currentBreakdown, setCurrentBreakdown] = useState([]);
  const [paymentHistoryModalOpen, setPaymentHistoryModalOpen] = useState(false);
  const [currentPaymentHistory, setCurrentPaymentHistory] = useState([]);
  const [selectedSummaryForHistory, setSelectedSummaryForHistory] = useState(null);
  const [editPaymentModalOpen, setEditPaymentModalOpen] = useState(false);
  const [editingPaymentRecord, setEditingPaymentRecord] = useState(null);
  const [editPaymentAmount, setEditPaymentAmount] = useState('');
  const [editPaymentDate, setEditPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [editPaymentNotes, setEditPaymentNotes] = useState('');
  const [confirmDeletePaymentModal, setConfirmDeletePaymentModal] = useState({ isOpen: false, paymentHistoryId: null, summaryIdToRefresh: null });
  const [confirmDeleteDuesModal, setConfirmDeleteDuesModal] = useState(false);

  const loadAllInitialData = useCallback(async () => {
    setIsDataLoading(true);
    try {
      const [defs, apts, ppl, blds, blks] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/dues-definitions`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }).then(res => res.json()),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/apartments`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }).then(res => res.json()),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/people`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }).then(res => res.json()),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/buildings`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }).then(res => res.json()),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blocks`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        }).then(res => res.json()),
      ]);
      setDuesDefinitions(defs || []);
      setApartments(Array.isArray(apts) ? apts : apts.recordset ?? apts.data ?? apts.apartments ?? []);
      setPeople(ppl || []);
      setBuildings(blds || []);
      setBlocks(blks || []);
    } catch (error) {
      showToast("Gerekli veriler yüklenirken hata: " + error.message, "error");
    } finally {
      setIsDataLoading(false);
    }
  }, [showToast]);

  const loadSummaries = useCallback(async () => {
    setIsLoading(true);
    try {
      const filtersToApply = {};
      if (currentUser && currentUser.buildingId) {
        filtersToApply.buildingId = currentUser.buildingId;
      }
      if (filterPeriod) filtersToApply.period = filterPeriod;
      if (filterBlockId) filtersToApply.blockId = filterBlockId;
      if (filterPersonId) filtersToApply.personId = filterPersonId;
      if (filterStatus) filtersToApply.status = filterStatus;
      if (filterResponsiblePartyType) filtersToApply.responsiblePartyType = filterResponsiblePartyType;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/periodical-dues-summaries?` + new URLSearchParams(filtersToApply), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
          window.location.reload();
          throw new Error('Oturum süresi doldu veya yetkiniz yok.');
        }
        throw new Error('Aidat özetleri yüklenemedi.');
      }
      const data = await response.json();
      setPeriodicalDuesSummaries(data || []);
    } catch (error) {
      showToast("Aidat özetleri yüklenirken hata: " + error.message, "error");
      setPeriodicalDuesSummaries([]);
    } finally {
      setIsLoading(false);
    }
  }, [showToast, currentUser, filterPeriod, filterBlockId, filterPersonId, filterStatus, filterResponsiblePartyType]);

  useEffect(() => {
    if (isDataLoading) return;
    loadSummaries();
  }, [filterPeriod, filterPersonId, filterStatus, filterBlockId, isDataLoading, loadSummaries]);

  useEffect(() => {
    loadAllInitialData();
  }, [loadAllInitialData]);

  const handleGenerateAllDuesForPeriod = async () => {
    if (!selectedPeriodForGeneration) {
      showToast("Lütfen borçlandırılacak bir dönem seçin.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/periodical-dues-summaries/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          period: selectedPeriodForGeneration,
          buildingId: currentUser?.buildingId,
          blockId: selectedBlockForGeneration,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Dönem aidatları oluşturulurken hata.');
      }
      const result = await response.json();
      showToast(result.message || "Dönem aidatları başarıyla işlendi.", "success", 6000);
      loadSummaries();
    } catch (error) {
      showToast("Dönem aidatları oluşturulurken hata: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDistribution = async () => {
    if (!distributionPeriod || !distributionTotalCost || !distributionMeterType) {
      showToast("Lütfen dönem, sayaç türü ve toplam gider tutarını girin.", "error");
      return;
    }
    if (distributionScope === 'block' && !distributionBlockId) {
      showToast("Lütfen blok seçin.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/periodical-dues-summaries/distribute-meter-based-dues`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          period: distributionPeriod,
          meterType: distributionMeterType,
          totalCost: parseFloat(distributionTotalCost),
          scope: distributionScope,
          buildingId: currentUser?.buildingId,
          blockId: distributionScope === 'block' ? distributionBlockId : null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Gider paylaştırma işlemi sırasında bir hata oluştu.');
      }

      const result = await response.json();
      showToast(result.message || "Giderler başarıyla paylaştırıldı ve aidatlar oluşturuldu.", "success", 6000);
      loadSummaries();
    } catch (error) {
      showToast("Gider paylaştırma hatası: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDuesForPeriod = async () => {
    if (!selectedPeriodForGeneration) {
      showToast("Lütfen silinecek dönemi seçin.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const result = await deleteDuesForPeriod(selectedPeriodForGeneration, currentUser?.buildingId, selectedBlockForGeneration);
      showToast(result.message || "Dönem aidatları başarıyla silindi.", "success", 6000);
      loadSummaries();
      setConfirmDeleteDuesModal(false);
    } catch (error) {
      if (error.message.includes("Seçtiğiniz döneme ait ödeme kayıtları bulunmaktadır")) {
        showToast(error.message, "error", 8000);
      } else {
        showToast("Dönem aidatları silinirken hata: " + error.message, "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getBuildingName = (bId) => buildings.find(b => b.id === bId)?.name || '';
  const getBlockName = (blId) => blocks.find(b => b.id === blId)?.name || '';
  const getPersonName = (pId) => people.find(p => p.id === pId)?.name || '';
  const getApartmentDetails = (aptId) => {
    if (!Array.isArray(apartments)) return { number: '?', buildingName: '?', blockName: '?' };
    const apt = apartments.find(a => a.id === aptId);
    return apt
      ? { number: apt.apartmentNumber, buildingName: getBuildingName(apt.buildingId), blockName: getBlockName(apt.blockId) }
      : { number: '?', buildingName: '?', blockName: '?' };
  };

  const openPaymentModal = (summary) => {
    setDueSummaryToPay(summary);
    setPaymentAmount('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentModalOpen(true);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!dueSummaryToPay) {
      showToast("Ödeme kaydı hatası.", "error");
      return;
    }
    const paid = parseFloat(paymentAmount);
    if (isNaN(paid) || paid <= 0) {
      showToast("Geçerli ödeme tutarı girin.", "error");
      return;
    }
    if (!paymentDate) {
      showToast("Ödeme tarihi seçin.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const paymentData = {
        paidAmount: paid,
        paymentDate: paymentDate,
        currentPaidAmount: dueSummaryToPay.paidAmount || 0,
        totalAmountDue: dueSummaryToPay.totalAmountDue
      };
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/periodical-dues-summaries/${dueSummaryToPay.id}/payments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ödeme eklenemedi.');
      }
      showToast("Ödeme başarıyla kaydedildi.", "success");
      loadSummaries();
      setPaymentModalOpen(false);
      setDueSummaryToPay(null);
    } catch (error) {
      showToast("Ödeme işlenirken hata: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const getResponsiblePartyTypeDisplay = (type) => ({ owner: 'Kat Maliki', resident: 'Sakin' }[type] || '');

  const openBreakdownModal = (summary) => {
    setCurrentBreakdown(summary.breakdown || []);
    setBreakdownModalOpen(true);
  };

  const openPaymentHistoryModal = async (summary) => {
    setSelectedSummaryForHistory(summary);
    setIsLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/periodical-dues-summaries/${summary.id}/payment-history`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Ödeme geçmişi yüklenemedi.');
      }
      const historyData = await response.json();
      setCurrentPaymentHistory(historyData || []);
      setPaymentHistoryModalOpen(true);
    } catch (error) {
      showToast("Ödeme geçmişi yüklenirken hata: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const openEditPaymentModal = (paymentRecord) => {
    setEditingPaymentRecord(paymentRecord);
    setEditPaymentAmount(paymentRecord.amountPaid.toString());
    setEditPaymentDate(new Date(paymentRecord.paymentDate).toISOString().split('T')[0]);
    setEditPaymentNotes(paymentRecord.notes || '');
    setEditPaymentModalOpen(true);
  };

  const handleUpdatePaymentRecord = async (e) => {
    e.preventDefault();
    if (!editingPaymentRecord) return;
    const updatedAmount = parseFloat(editPaymentAmount);
    if (isNaN(updatedAmount) || updatedAmount <= 0) {
      showToast("Geçerli bir ödeme tutarı girin.", "error");
      return;
    }
    if (!editPaymentDate) {
      showToast("Ödeme tarihi seçin.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/periodical-dues-summaries/payment-history/${editingPaymentRecord.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amountPaid: updatedAmount,
          paymentDate: editPaymentDate,
          notes: editPaymentNotes,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ödeme kaydı güncellenemedi.');
      }
      showToast("Ödeme kaydı başarıyla güncellendi.", "success");
      setEditPaymentModalOpen(false);
      setEditingPaymentRecord(null);
      if (selectedSummaryForHistory) {
        openPaymentHistoryModal(selectedSummaryForHistory);
      }
      loadSummaries();
    } catch (error) {
      showToast("Ödeme kaydı güncellenirken hata: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePaymentRecord = async (paymentHistoryId, summaryId) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/periodical-dues-summaries/payment-history/${paymentHistoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ödeme kaydı silinemedi.');
      }
      showToast("Ödeme kaydı başarıyla silindi.", "success");
      setConfirmDeletePaymentModal({ isOpen: false, paymentHistoryId: null, summaryIdToRefresh: null });
      if (selectedSummaryForHistory && selectedSummaryForHistory.id === summaryId) {
        openPaymentHistoryModal(selectedSummaryForHistory);
      }
      loadSummaries();
    } catch (error) {
      showToast("Ödeme kaydı silinirken hata: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Filter blocks for generation based on the current user's building
  const blocksForGenFilter = currentUser && currentUser.buildingId && blocks.length > 0
    ? blocks.filter(block => String(block.buildingId) === String(currentUser.buildingId))
    : [];
  // Filter blocks for the summary table based on the current user's building
  const blocksForTableFilter = currentUser && currentUser.buildingId && blocks.length > 0
    ? blocks.filter(block => String(block.buildingId) === String(currentUser.buildingId))
    : [];

  // Tarih formatlama fonksiyonu
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
  };

  if (isDataLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-600">Veriler yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-30">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="ml-3 text-gray-700">İşlem yapılıyor...</p>
        </div>
      )}
      <h1 className="text-2xl font-semibold mb-6">Aidat Oluşturma ve Takip (Dönemlik Özetler)</h1>
      {/* Aidat Oluşturma Bölümü */}
      <div className="mb-6 p-4 border rounded-lg bg-gray-50">
        <h2 className="text-xl font-semibold mb-3">Seçili Dönem İçin Tüm Tanımlı Aidatları Borçlandır</h2>
        <div className="grid md:grid-cols-2 gap-4 items-end">
          <div>
            <label htmlFor="genPeriodSelect" className="label-form-style">Dönem Seçin (YYYY-AA):</label>
            <input
              type="month"
              id="genPeriodSelect"
              value={selectedPeriodForGeneration}
              onChange={(e) => setSelectedPeriodForGeneration(e.target.value)}
              className="input-form-style"
            />
          </div>
          <div>
            <label htmlFor="genBlockFilter" className="label-form-style">Blok Filtresi (Opsiyonel):</label>
            <select
              id="genBlockFilter"
              value={selectedBlockForGeneration}
              onChange={(e) => setSelectedBlockForGeneration(e.target.value)}
              className="input-form-style"
              disabled={blocksForGenFilter.length === 0}
            >
              <option value="">Tüm Bloklar</option>
              {blocksForGenFilter.map(bl => <option key={bl.id} value={bl.id}>{bl.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex space-x-4 mt-4">
          <button
            onClick={handleGenerateAllDuesForPeriod}
            className="btn-primary-style px-4 py-2 flex items-center justify-center flex-grow"
            disabled={isLoading || !selectedPeriodForGeneration || (apartments && apartments.length === 0 && !isDataLoading)}
          >
            {isLoading && !isDataLoading ? <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div> : <PlusCircle size={18} className="mr-2" />}
            Dönem Aidatlarını Oluştur/Güncelle
          </button>
          <button
            onClick={() => setConfirmDeleteDuesModal(true)}
            className="btn-secondary-style px-4 py-2 flex items-center justify-center flex-grow bg-red-500 hover:bg-red-600 text-white"
            disabled={isLoading || !selectedPeriodForGeneration}
          >
            <XCircle size={18} className="mr-2" /> Dönem Aidatlarını Sil
          </button>
          <button
            onClick={() => setCurrentView('duesReport')}
            className="btn-secondary-style px-4 py-2 flex items-center justify-center flex-grow"
            disabled={isLoading || isDataLoading}
          >
            <FileText size={18} className="mr-2" /> Aidat Raporu Oluştur
          </button>
        </div>
        {apartments && apartments.length === 0 && !isDataLoading && <p className="text-sm text-red-500 mt-2">Aidat oluşturmak için önce sisteme daire eklenmiş olmalıdır.</p>}
      </div>
      {/* Confirm Delete Dues Modal */}
      <ConfirmationModal
        isOpen={confirmDeleteDuesModal}
        onClose={() => setConfirmDeleteDuesModal(false)}
        title="Dönem Aidatlarını Sil"
        message={`Seçili dönem (${selectedPeriodForGeneration}) için tüm aidatları silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve tüm borç kayıtlarını ve dökümlerini silecektir.`}
        onConfirm={handleDeleteDuesForPeriod}
      />

      {/* Sayaç Okumalarına Göre Gider Paylaştırma */}
      <div className="my-8 p-4 border rounded-lg bg-gray-50">
        <h2 className="text-xl font-semibold mb-3">Sayaç Okumalarına Göre Gider Paylaştırma</h2>
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div>
            <label htmlFor="distPeriod" className="label-form-style">Dönem (YYYY-AA):</label>
            <input
              type="month"
              id="distPeriod"
              value={distributionPeriod}
              onChange={(e) => setDistributionPeriod(e.target.value)}
              className="input-form-style"
            />
          </div>
          <div>
            <label htmlFor="distMeterType" className="label-form-style">Sayaç Türü:</label>
            <select
              id="distMeterType"
              value={distributionMeterType}
              onChange={(e) => setDistributionMeterType(e.target.value)}
              className="input-form-style"
            >
              <option value="yakit">Yakıt</option>
              <option value="su">Su</option>
            </select>
          </div>
          <div>
            <label htmlFor="distTotalCost" className="label-form-style">Toplam Gider Tutarı (TL):</label>
            <input
              type="number"
              id="distTotalCost"
              value={distributionTotalCost}
              onChange={(e) => setDistributionTotalCost(e.target.value)}
              className="input-form-style"
              placeholder="Örn: 5000"
            />
          </div>
          <div>
            <label htmlFor="distScope" className="label-form-style">Uygulama Kapsamı:</label>
            <select
              id="distScope"
              value={distributionScope}
              onChange={(e) => {
                setDistributionScope(e.target.value);
                if (e.target.value === 'building') {
                  setDistributionBlockId('');
                }
              }}
              className="input-form-style"
            >
              <option value="building">Tüm Bina</option>
              <option value="block">Blok Bazında</option>
            </select>
          </div>
          {distributionScope === 'block' && (
            <div>
              <label htmlFor="distBlock" className="label-form-style">Blok Seçin:</label>
              <select
                id="distBlock"
                value={distributionBlockId}
                onChange={(e) => setDistributionBlockId(e.target.value)}
                className="input-form-style"
                disabled={blocksForGenFilter.length === 0}
              >
                <option value="">Blok Seçin</option>
                {blocksForGenFilter.map(bl => <option key={bl.id} value={bl.id}>{bl.name}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="mt-4">
          <button
            onClick={handleDistribution}
            className="btn-primary-green-style w-full"
            disabled={isLoading || !distributionPeriod || !distributionTotalCost || (distributionScope === 'block' && !distributionBlockId)}
          >
            Gideri Paylaştır ve Aidat Oluştur
          </button>
        </div>
      </div>

      <hr className="my-8"/>
      <h2 className="text-xl font-semibold mb-4">Dönemlik Aidat Özetleri</h2>
      {/* Filtreleme Bölümü */}
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6 p-4 border rounded-lg bg-gray-50">
        <div>
          <label htmlFor="filterPeriodSum" className="label-form-style">Dönem:</label>
          <input
            type="month"
            id="filterPeriodSum"
            value={filterPeriod}
            onChange={(e) => setFilterPeriod(e.target.value)}
            className="input-form-style"
          />
        </div>
        <div>
          <label htmlFor="filterBlockSum" className="label-form-style">Blok:</label>
          <select
            id="filterBlockSum"
            value={filterBlockId}
            onChange={(e) => setFilterBlockId(e.target.value)}
            className="input-form-style"
            disabled={blocksForTableFilter.length === 0}
          >
            <option value="">Tüm Bloklar</option>
            {blocksForTableFilter.map(bl => <option key={bl.id} value={bl.id}>{bl.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="filterPersonSum" className="label-form-style">Kişi:</label>
          <select
            id="filterPersonSum"
            value={filterPersonId}
            onChange={(e) => setFilterPersonId(e.target.value)}
            className="input-form-style"
          >
            <option value="">Tüm Kişiler</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="filterStatusSum" className="label-form-style">Durum:</label>
          <select
            id="filterStatusSum"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input-form-style"
          >
            <option value="">Tüm Durumlar</option>
            <option value="ödenmedi">Ödenmedi</option>
            <option value="kısmi ödendi">Kısmi Ödendi</option>
            <option value="ödendi">Ödendi</option>
          </select>
        </div>
        <div>
          <label htmlFor="filterResponsiblePartyTypeSum" className="label-form-style">Sorumlu Rolü:</label>
          <select
            id="filterResponsiblePartyTypeSum"
            value={filterResponsiblePartyType}
            onChange={(e) => setFilterResponsiblePartyType(e.target.value)}
            className="input-form-style"
          >
            <option value="">Tüm Roller</option>
            <option value="owner">Kat Maliki</option>
            <option value="resident">Sakin</option>
          </select>
        </div>
      </div>
      {/* Tablo */}
      {isDataLoading && <p className="text-gray-500 text-center py-4">Temel veriler yükleniyor...</p>}
      {!isDataLoading && isLoading && <p className="text-gray-500 text-center py-4">Aidat özetleri yükleniyor...</p>}
      {!isDataLoading && !isLoading && periodicalDuesSummaries.length === 0 ? (
        <p className="text-gray-500 text-center py-4">Gösterilecek aidat özeti bulunamadı veya filtrenizle eşleşen kayıt yok.</p>
      ) : null}
      {!isLoading && periodicalDuesSummaries.length > 0 ? (
        <div className="overflow-x-auto shadow border-b border-gray-200 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Dönem</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Bina / Blok / Daire</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Borçlu Kişi (Rolü)</th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Toplam Borç</th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Ödenen</th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Kalan</th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Durum</th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {periodicalDuesSummaries.map(s => {
                const aptD = getApartmentDetails(s.apartmentId);
                const remainingAmount = (s.totalAmountDue || 0) - (s.paidAmount || 0);
                return (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{s.period}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      <div className="font-medium">{s.buildingName || aptD.buildingName} / {s.blockName || aptD.blockName}</div>
                      <div className="text-xs text-gray-500">D: {s.apartmentNumber || aptD.number}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {s.personName || getPersonName(s.personId)}
                      <span className="block text-xs text-gray-500">({getResponsiblePartyTypeDisplay(s.responsiblePartyType)})</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-800 text-right font-semibold">{(s.totalAmountDue || 0).toFixed(2)} TL</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600 text-right">{(s.paidAmount || 0).toFixed(2)} TL</td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm font-semibold text-right ${remainingAmount > 0 ? 'text-red-600' : 'text-gray-700'}`}>
                      {remainingAmount.toFixed(2)} TL
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        s.status === 'ödendi' ? 'bg-green-100 text-green-800' :
                        s.status === 'kısmi ödendi' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {s.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center space-x-1">
                      <button onClick={() => openBreakdownModal(s)} className="icon-button-blue p-1" title="Borç Detayları">
                        <ListChecks size={18} />
                      </button>
                      <button onClick={() => openPaymentHistoryModal(s)} className="icon-button-purple p-1" title="Ödeme Geçmişi">
                        <Eye size={18} />
                      </button>
                      {s.status !== 'ödendi' && (
                        <button onClick={() => openPaymentModal(s)} className="icon-button-green p-1" title="Ödeme Yap">
                          <CreditCard size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {/* Payment Modal */}
      {dueSummaryToPay && (
        <Modal isOpen={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} title={`Ödeme Kaydı: ${dueSummaryToPay.personName} (${dueSummaryToPay.period})`} size="lg">
          <form onSubmit={handlePayment}>
            <div className="mb-4 text-sm">
              <p><strong>Bina/Daire:</strong> {getApartmentDetails(dueSummaryToPay.apartmentId).buildingName} / {getApartmentDetails(dueSummaryToPay.apartmentId).blockName} / D: {getApartmentDetails(dueSummaryToPay.apartmentId).number}</p>
              <p><strong>Toplam Dönem Borcu:</strong> {dueSummaryToPay.totalAmountDue.toFixed(2)} TL</p>
              <p><strong>Daha Önce Ödenen:</strong> {(dueSummaryToPay.paidAmount || 0).toFixed(2)} TL</p>
              <p className="font-semibold text-base"><strong>Kalan Borç:</strong> {(dueSummaryToPay.totalAmountDue - (dueSummaryToPay.paidAmount || 0)).toFixed(2)} TL</p>
            </div>
            <div className="my-4 py-2 border-y">
              <h4 className="text-md font-semibold mb-1">Bu Döneme Ait Borç Kalemleri:</h4>
              {(dueSummaryToPay.breakdown && dueSummaryToPay.breakdown.length > 0) ? (
                <ul className="list-disc list-inside text-xs max-h-32 overflow-y-auto">
                  {(dueSummaryToPay.breakdown || []).map(i => <li key={i.definitionId || i.definitionName}>{i.definitionName}: {i.amount.toFixed(2)}TL</li>)}
                </ul>
              ) : <p className="text-xs text-gray-500">Borç dökümü bulunamadı.</p>}
            </div>
            <div className="mb-4">
              <label htmlFor="payAmountModalGenSum" className="label-form-style">Ödeme Tutarı (TL):</label>
              <input
                type="number"
                step="0.01"
                id="payAmountModalGenSum"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="input-form-style"
                max={(dueSummaryToPay.totalAmountDue - (dueSummaryToPay.paidAmount || 0)).toFixed(2)}
                required
              />
            </div>
            <div className="mb-6">
              <label htmlFor="payDateModalGenSum" className="label-form-style">Ödeme Tarihi:</label>
              <input
                type="date"
                id="payDateModalGenSum"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="input-form-style"
                required
              />
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <button type="button" onClick={() => setPaymentModalOpen(false)} className="btn-secondary-style px-4 py-2">
                İptal
              </button>
              <button type="submit" className="btn-primary-green-style px-4 py-2">
                Ödemeyi Kaydet
              </button>
            </div>
          </form>
        </Modal>
      )}
      {/* Payment History Modal */}
      {/* Payment History Modal */}
{selectedSummaryForHistory && (
  <Modal
    isOpen={paymentHistoryModalOpen}
    onClose={() => {
      setPaymentHistoryModalOpen(false);
      setSelectedSummaryForHistory(null);
    }}
    title={`Ödeme Geçmişi: ${selectedSummaryForHistory.personName} (${selectedSummaryForHistory.period})`}
    size="2xl"
  >
    {isLoading && (
      <div className="flex justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    )}
    {!isLoading && currentPaymentHistory.length === 0 && (
      <p className="text-gray-500">Bu aidat dönemi için ödeme geçmişi bulunmamaktadır.</p>
    )}
    {!isLoading && currentPaymentHistory.length > 0 && (
      <div className="overflow-x-auto max-h-[60vh]">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="th-style">Ödeme Tarihi</th>
              <th className="th-style text-right">Tutar (TL)</th>
              <th className="th-style">Notlar</th>
              <th className="th-style">Kayıt Tarihi</th>
              <th className="th-style text-center">İşlemler</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentPaymentHistory.map((ph) => (
              <tr key={ph.id}>
                <td className="td-style">{new Date(ph.paymentDate).toLocaleDateString('tr-TR')}</td>
                <td className="td-style text-right">{ph.amountPaid.toFixed(2)}</td>
                <td className="td-style max-w-xs truncate" title={ph.notes}>
                  {ph.notes || '-'}
                </td>
                <td className="td-style">{formatDate(ph.recordedAt)}</td>
                <td className="td-style text-center space-x-2">
                  <button
                    onClick={() => openEditPaymentModal(ph)}
                    className="icon-button-indigo"
                    title="Düzenle"
                  >
                    <Edit3 size={16} />
                  </button>
                  <button
                    onClick={() =>
                      setConfirmDeletePaymentModal({
                        isOpen: true,
                        paymentHistoryId: ph.id,
                        summaryIdToRefresh: selectedSummaryForHistory.id,
                      })
                    }
                    className="icon-button-red"
                    title="Sil"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    <div className="mt-6 flex justify-end">
      <button
        type="button"
        onClick={() => {
          setPaymentHistoryModalOpen(false);
          setSelectedSummaryForHistory(null);
        }}
        className="btn-primary-style"
      >
        Kapat
      </button>
    </div>
  </Modal>
)}

      {/* Edit Payment Modal */}
      {editingPaymentRecord && (
        <Modal isOpen={editPaymentModalOpen} onClose={() => {setEditPaymentModalOpen(false); setEditingPaymentRecord(null);}} title="Ödeme Kaydını Düzenle" size="lg">
          <form onSubmit={handleUpdatePaymentRecord}>
            <div className="mb-4">
              <label htmlFor="editPayAmountModal" className="label-form-style">Ödeme Tutarı (TL):</label>
              <input
                type="number"
                step="0.01"
                id="editPayAmountModal"
                value={editPaymentAmount}
                onChange={(e) => setEditPaymentAmount(e.target.value)}
                className="input-form-style"
                required
              />
            </div>
            <div className="mb-4">
              <label htmlFor="editPayDateModal" className="label-form-style">Ödeme Tarihi:</label>
              <input
                type="date"
                id="editPayDateModal"
                value={editPaymentDate}
                onChange={(e) => setEditPaymentDate(e.target.value)}
                className="input-form-style"
                required
              />
            </div>
            <div className="mb-6">
              <label htmlFor="editPayNotesModal" className="label-form-style">Notlar (Opsiyonel):</label>
              <textarea
                id="editPayNotesModal"
                value={editPaymentNotes}
                onChange={(e) => setEditPaymentNotes(e.target.value)}
                rows="3"
                className="input-form-style"
              ></textarea>
            </div>
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={() => {setEditPaymentModalOpen(false); setEditingPaymentRecord(null);}} className="btn-secondary-style">İptal</button>
              <button type="submit" className="btn-primary-green-style">Güncelle</button>
            </div>
          </form>
        </Modal>
      )}
      {/* Confirm Delete Payment Modal */}
      <ConfirmationModal
        isOpen={confirmDeletePaymentModal.isOpen}
        onClose={() => setConfirmDeletePaymentModal({ isOpen: false, paymentHistoryId: null, summaryIdToRefresh: null })}
        title="Ödeme Kaydını Sil"
        message="Bu ödeme kaydını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        onConfirm={() => handleDeletePaymentRecord(confirmDeletePaymentModal.paymentHistoryId, confirmDeletePaymentModal.summaryIdToRefresh)}
      />
      {/* Breakdown Modal */}
      <Modal isOpen={breakdownModalOpen} onClose={() => setBreakdownModalOpen(false)} title="Dönem Borç Dökümü" size="lg">
        {(currentBreakdown && currentBreakdown.length > 0) ? (
          <ul className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
            {currentBreakdown.map((i, idx) => (
              <li key={i.definitionId || idx} className="py-3 flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">{i.definitionName} ({i.type === 'arsaPayi' ? 'Arsa Paylı' : (i.type === 'sabitTutar' ? 'Sabit' : 'Diğer')})</span>
                <span className="text-sm text-gray-900 font-semibold">{i.amount.toFixed(2)}TL</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-xs text-gray-500">Borç dökümü bulunamadı.</p>}
        <div className="mt-6 flex justify-end">
          <button type="button" onClick={() => setBreakdownModalOpen(false)} className="btn-primary-style">Kapat</button>
        </div>
      </Modal>
    </div>
  );
};

export default GenerateDuesView;
